'use client'

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { AppCombobox } from '@/components/elementary/AppCombobox'
import { AppForm } from '@/components/forms/AppForm'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/i18n/navigation'
import { companySizeKeys, industryKeys, languageKeys } from '@/lib/constants'
import { CASE_STUDIES_FILTERS_TEST_IDS } from '@/lib/test-ids'

export function CaseStudyFilterForm({
	integrations,
}: {
	integrations: Data.ContentType<'api::integration.integration'>[]
}) {
	const t = useTranslations('caseStudiesFilters')
	const tIndustry = useTranslations('industry')
	const tCompanySize = useTranslations('companySize')
	const tLanguage = useTranslations('language')

	const router = useRouter()

	const form = useForm<z.infer<FormSchemaType>>({
		resolver: standardSchemaResolver(FilterFormSchema),
		mode: 'onBlur',
		reValidateMode: 'onSubmit',
	})

	const industryOptions = industryKeys.map((key) => ({
		value: key,
		label: tIndustry(key),
	}))

	const companySizeOptions = companySizeKeys.map((key) => ({
		value: key,
		label: tCompanySize(key),
	}))

	const languagesOptions = languageKeys.map((key) => ({
		value: key,
		label: tLanguage(key),
	}))

	const integrationOptions = integrations.map(
		(item: Data.ContentType<'api::integration.integration'>) => ({
			value: item.documentId,
			label: item.title ?? '',
		}),
	)

	const buildQueryParams = (values: z.infer<FormSchemaType>) => {
		const params: Record<string, string[]> = {}

		const mappings: [keyof typeof values, string][] = [
			['industry', 'industry'],
			['integrations', 'integrations'],
			['company', 'company_size'],
			['language', 'target_languages'],
		]

		for (const [key, queryParam] of mappings) {
			const valueArray = values[key]
			if (Array.isArray(valueArray) && valueArray.length) {
				params[queryParam] = valueArray
			}
		}

		return params
	}

	const onSubmit = (values: z.infer<FormSchemaType>) => {
		const queryParams = buildQueryParams(values)
		router.push(
			{
				pathname: '/case-studies',
				query: queryParams,
			},
			{ scroll: false },
		)
	}

	const clearFilters = useCallback(() => {
		form.reset()
		router.push({ pathname: '/case-studies' }, { scroll: false })
	}, [form, router])

	return (
		<div className="p-4 gap-5 lg:gap-3 items-center flex lg:p-8 lg:flex-row flex-col justify-between rounded-xl bg-peach-300">
			<AppForm
				form={form}
				id={filterFormName}
				onSubmit={onSubmit}
				className="flex w-full"
				fieldsetClassname="gap-5 lg:gap-3 w-full flex items-center lg:flex-row flex-col justify-between"
			>
				<div className="gap-2 lg:gap-3 lg:flex lg:flex-wrap md:whitespace-nowrap lg:flex-row grid grid-cols-1 sm:grid-cols-2 w-full lg:max-w-180">
					<AppCombobox
						name="industry"
						values={industryOptions}
						noDataMessage="Nothing matched your query."
						placeholder={t('industry')}
						data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_TRIGGER}
						containerClassName="w-full md:flex-1"
						fieldClassName="w-full pr-1"
					/>
					<AppCombobox
						name="integrations"
						values={integrationOptions}
						noDataMessage="No integrations found."
						placeholder={t('integrations')}
						data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_TRIGGER}
						containerClassName="w-full md:flex-1"
						fieldClassName="w-full pr-1"
					/>
					<AppCombobox
						name="company"
						values={companySizeOptions}
						noDataMessage="Nothing matched your query."
						placeholder={t('companySize')}
						data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_TRIGGER}
						containerClassName="w-full md:flex-1"
						fieldClassName="w-full pr-1"
					/>
					<AppCombobox
						name="language"
						values={languagesOptions}
						noDataMessage="Nothing matched your query."
						placeholder={t('targetLanguages')}
						data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_TRIGGER}
						containerClassName="w-full md:flex-1"
						fieldClassName="w-full pr-1"
					/>
				</div>

				<div className="flex justify-between flex-col sm:flex-row flex-1 whitespace-nowrap gap-2">
					<Button type="submit" data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_APPLY_FILTERS}>
						{t('applyFilters')}
					</Button>
					<Button
						type="button"
						variant="primaryLink"
						onClick={clearFilters}
						data-test-id={CASE_STUDIES_FILTERS_TEST_IDS.FILTER_CLEAR_FILTERS}
					>
						<Paragraph
							fontSize="bodyRegularMedium"
							className="underline hover:text-brand-salmon active:text-brand-salmon"
						>
							{t('clearFilters')}
						</Paragraph>
					</Button>
				</div>
			</AppForm>
		</div>
	)
}
const FilterFormSchema = z.object({
	industry: z.array(z.string()).optional(),
	integrations: z.array(z.string()).optional(),
	company: z.array(z.string()).optional(),
	language: z.array(z.string()).optional(),
})
type FormSchemaType = typeof FilterFormSchema

export const filterFormName = 'caseStudiesFilters'
